import React, { useState, useEffect, useMemo, useRef } from 'react';
import Sidebar from '../components/Sidebar'; // adjust path as necessary
import {
  FaDownload,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaSave
} from 'react-icons/fa';
import axios from 'axios'; // Using axios for easier multipart/json if needed, or fetch as per sample
import { useScreenPermissions } from '../components/useScreenPermissions'; // Adjust path
import { useNavigate } from 'react-router-dom';
import SurveyBOQFormModal from './SurveyBOQFormModal';
import SurveyBOQViewModal from './SurveyBOQViewModal';

const API_URL = '/api'; // Adjust port as necessary

// --- ICONS ---
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const SaveIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);

// Add this component
const SuccessIcon = ({ size = 48, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

// --- STYLES & THEME ---
const COLORS = {
  CLOUD: '#F5EFE6',
  BABY: '#F0ECE2',
  BABY1: '#B88A5A',
  DIVIDER: '#E6D9CC',
  BORDER: '#E6D9CC',
  TEXT: '#211f1eff',
  MUTED: '#6E584A',
  BADGE_BG: '#F6EFE4',
  SUCCESS_BG: '#EAF3EE',
  WARNING_BG: '#FBF2E8',
  PRIMARY_HOVER: '#A27246'
};

const ActionModal = ({ isOpen, status, message, onClose }) => {
  if (!isOpen) return null;

  let icon, title, bgColor, textColor;

  if (status === 'loading') {
    bgColor = 'bg-blue-100';
    textColor = 'text-blue-600';
    title = 'Processing...';
    icon = <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>;
  } else if (status === 'success') {
    bgColor = 'bg-green-100';
    textColor = 'text-green-600';
    title = 'Done';
    icon = (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
      </svg>
    );
  } else {
    // Error
    bgColor = 'bg-red-100';
    textColor = 'text-gray-500';
    title = 'Error';
    icon = (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
      </svg>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity"></div>
      
      <div className="relative bg-white shadow-xl max-w-sm w-full mx-4 p-6 text-center animate-in fade-in zoom-in duration-200 ">
        <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${bgColor} mb-4`}>
          {icon}
        </div>
        
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {title}
        </h3>
        
        
<p className={`text-sm ${textColor} mb-6`}>
  {message}
</p>
        
        {status === 'error' && (
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
};

// Add this component after ActionModal
const ConfirmationDialog = ({ isOpen, title, message, onConfirm, onCancel, isDanger = false }) => {
  if (!isOpen) return null;

  const iconBgColor = isDanger ? 'bg-red-100' : 'bg-teal-100';
  const iconTextColor = isDanger ? 'text-red-600' : 'text-teal-600';
  
  const confirmButtonClass = isDanger 
    ? 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500' 
    : 'bg-teal-700 hover:bg-teal-800 text-white focus:ring-teal-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity"
        onClick={onCancel}
      ></div>
      
      <div className="relative bg-white shadow-xl max-w-sm w-full mx-4 p-6 text-center animate-in fade-in zoom-in duration-200">
        <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${iconBgColor} mb-4`}>
          {isDanger ? (
            <svg className={`h-6 w-6 ${iconTextColor}`} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          ) : (
            <svg className={`h-6 w-6 ${iconTextColor}`} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
            </svg>
          )}
        </div>
        
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-6">{message}</p>
        
        <div className="flex justify-center gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-medium rounded transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${confirmButtonClass}`}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'numeric',
    year: 'numeric'
  });
};

// --- MAIN COMPONENT ---
const SurveyBOQItems = () => {
  // CSS Variables injection
  useEffect(() => {
    const themeVars = {
      '--sidebar-line-color': COLORS.BORDER,
      '--sidebar-bg': '#ffffff',
      '--page-background': COLORS.CLOUD,
      '--accent-color': COLORS.BABY1,
      '--accent-hover': COLORS.PRIMARY_HOVER,
      '--page-border-color': COLORS.BORDER,
      '--text-primary': COLORS.TEXT,
      '--text-muted': COLORS.MUTED,
      '--text-muted-2': COLORS.MUTED,
      '--disabled-color': '#C9BFB3',
      '--search-icon': COLORS.MUTED,
      '--hover-gray': COLORS.BADGE_BG
    };
    Object.entries(themeVars).forEach(([k, v]) => {
      document.documentElement.style.setProperty(k, v);
    });
  }, []);

  // Scoped CSS
  useEffect(() => {
    const styleId = 'surveyboq-overrides';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      .surveyboq-root .rounded-md, .surveyboq-root .rounded-lg { border-radius: 4px !important; }
      .surveyboq-root input, .surveyboq-root select { background: #ffffff !important; border: 1px solid ${COLORS.BORDER} !important; color: ${COLORS.TEXT} !important; }
      .surveyboq-root table { border-collapse: separate; border-spacing: 0; width: 100%; }
      .surveyboq-root table tbody tr { position: relative; }
      .surveyboq-root table tbody tr::after { content: ""; position: absolute; left: 0; right: 0; height: 1px; background: ${COLORS.DIVIDER}; bottom: 0; pointer-events: none; }
      .surveyboq-root table tbody td { border-bottom: none; position: relative; z-index: 1; }
      .surveyboq-root .status-badge { border-radius: 9999px !important; display: inline-flex !important; align-items: center; padding: 0.25rem 0.75rem; font-size: 12px; font-weight: 600; }
      .surveyboq-root .sidebar img, .surveyboq-root aside img { border-radius: 9999px !important; }
    `;
    document.head.appendChild(style);
  }, []);

  // State
  const [surveyBOQs, setSurveyBOQs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();
  const [approvalModal, setApprovalModal] = useState({
    isOpen: false,
    surveyBOQID: null, // Changed from mrnNumber
    currentLevel: null,
    remarks: ''
  });
  
  // --- ADDED: Multi-select filter state for BC Status ---
  const [selectedStatusFilters, setSelectedStatusFilters] = useState([]);
  // -----------------------------------------------------

  const [showFormModal, setShowFormModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBOQ, setSelectedBOQ] = useState(null); // For Edit/View
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

    // Add this state inside SurveyBOQItems component
  const [actionModal, setActionModal] = useState({
    isOpen: false,
    status: 'loading', // 'loading' | 'success' | 'error'
    message: ''
  });

    // Add this state inside SurveyBOQItems component
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    isDanger: false
  });

  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    id: null,
    boqNo: '',
    action: ''
  });

    const [toast, setToast] = useState({
    open: false,
    type: 'error',
    title: '',
    message: ''
  });

    // Add this state near other useState hooks
  const [summaryData, setSummaryData] = useState({
    projectQty: 0,
    surveyQty: 0,
    indentQty: 0,
    pendingApproval: 0,
    pendingSendToBC: 0
  });
  
  // Sorting & Pagination
  const [sortConfig, setSortConfig] = useState({ key: 'CreatedAt', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  const navigate = useNavigate();
  // const { hasPermission } = useScreenPermissions(); // Uncomment if hook is available


    // Add this function below fetchSurveyBOQs
  const fetchQuantityStats = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/survey-boq/quantity-stats-filter`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch stats');
      
      const data = await response.json();
      
      setSummaryData(prev => ({
        ...prev,
        projectQty: data.projectQty || 0,
        surveyQty: data.surveyQty || 0,
        indentQty: data.indentQty || 0
      }));
    } catch (error) {
      // console.error('Error fetching quantity stats:', error);
    }
  };

  // Fetch Data
  const fetchSurveyBOQs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/survey-boq-filter`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();
      const listData = Array.isArray(data) ? data : [];
      setSurveyBOQs(listData);
      // console.log("Fetched Survey BOQs:", data);
      // setSurveyBOQs(Array.isArray(data) ? data : []);
      // --- ADDED: Calculate Pending Counts ---
      const pendingApproval = listData.filter(item => item.ApprovalStatus === 'PENDING').length;
      
      const pendingSendToBC = listData.filter(item => {
        const isApprovedOrNeutral = item.ApprovalStatus !== 'PENDING' && item.ApprovalStatus !== 'REJECTED';
        return isApprovedOrNeutral && !item.SendToBc;
      }).length;

      setSummaryData(prev => ({
        ...prev,
        pendingApproval,
        pendingSendToBC
      }));
    } catch (error) {
      // console.error('Error fetching Survey BOQs:', error);
      // alert('Error loading data');
      showToast('Error loading data', 'error', 'Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSurveyBOQs();fetchQuantityStats(); }, []);

  // Handlers
  const handleCreate = () => {
    setSelectedBOQ(null);
    setShowFormModal(true);
  };

 const handleEdit = async (boq) => {
  try {
    // 1. Fetch the full details for this specific ID
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/survey-boq/${boq.SurveyBOQID}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (!response.ok) throw new Error('Failed to fetch details');
    
    const fullData = await response.json(); 
    // fullData structure will be: { header: { SiteEngineer: 123, ... }, lines: [...] }

    // 2. Set this full data to the state
    setSelectedBOQ(fullData); 
    setShowFormModal(true);
    
  } catch (error) {
    // console.error('Error fetching details:', error);
    // alert('Could not load record details');
    showToast('Could not load record details', 'error', 'Error');
  }
};


  const handleApprove = async (id) => {
    if (!window.confirm('Are you sure you want to approve this BOQ?')) return;
    
    try {
      const token = localStorage.getItem('authToken');
      // Using fetch as per your existing pattern, or axios if you prefer
      const response = await fetch(`${API_URL}/mapping/survey-boq/approve`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ surveyBOQID: id })
      });

      if (!response.ok) throw new Error('Failed to approve');
      
      const result = await response.json();
      if (result.success) {
        // alert('BOQ Approved successfully');
        showToast('BOQ Approved successfully', 'success', 'Done');
        fetchSurveyBOQs(); // Refresh list to update UI
      } else {
        showToast(result.message || 'Error approving', 'error', 'Error');
      }
    } catch (error) {
      // console.error('Error approving:', error);
      showToast('Error approving record', 'error', 'Error');
    }
  };

    const handleSendToBc = (boq) => {
    if (boq.SendToBc === 1) {
      // alert("This BOQ has already been sent to BC.");
      showToast('This BOQ has already been sent to BC.', 'error', 'Action Not Allowed');
      return;
    }

    // Use the custom Confirmation Modal instead of window.confirm
    setConfirmModal({
      isOpen: true,
      title: 'Send to BC',
      message: `Are you sure you want to send BOQ ${boq.SurveyBOQNo} to Business Central?`,
      isDanger: false,
      onConfirm: async () => {
        // 1. Close the confirmation modal
        setConfirmModal({ ...confirmModal, isOpen: false });

        // 2. Start Loading (ActionModal)
        setActionModal({ isOpen: true, status: 'loading', message: 'Sending to Business Central...' });

        try {
          const token = localStorage.getItem('authToken');
          const response = await fetch(`${API_URL}/mapping/survey-boq/send-to-bc`, {
            method: 'POST',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ surveyBOQID: boq.SurveyBOQID })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || errorData.error || 'Failed to send to BC');
          }
          
          const result = await response.json();
          
          if (result.success) {
            fetchSurveyBOQs();
            // 3. Show Success (ActionModal)
            setActionModal({ isOpen: true, status: 'success', message: 'BOQ sent to BC successfully!' });
            setTimeout(() => setActionModal({ isOpen: false }), 2000);
            // showToast('BOQ sent to BC successfully!', 'success', 'Done');
          } else {
            // 4. Show Error (ActionModal)
            setActionModal({ isOpen: true, status: 'error', message: 'Error: ' + result.message });
            // showToast('Error: ' + (result.message || 'Failed to send to BC'), 'error', 'Error');
          }
        } catch (error) {
          // console.error('Error sending to BC:', error);
          setActionModal({ isOpen: true, status: 'error', message: 'Error sending record to BC: ' + error.message });
          // showToast('Error sending record to BC: ' + error.message, 'error', 'Error');
        }
      }
    });
  };

    // 1. Replace your existing handleApprove with this:
  const openApprovalModal = (surveyBOQID, level) => {
    setApprovalModal({
      isOpen: true,
      surveyBOQID: surveyBOQID,
      currentLevel: level,
      remarks: ''
    });
  };

  // 2. Add this new handler for the API call:
   const handleApprovalAction = async (action) => {
    const { surveyBOQID, remarks } = approvalModal;

    if (action === 'REJECTED' && !remarks.trim()) {
      // alert('Remarks are mandatory when rejecting.');
      showToast('Remarks are mandatory when rejecting.', 'error', 'Validation Error');
      return;
    }
    if (action === 'APPROVED' && !remarks.trim()) {
      // alert('Remarks are mandatory when approving.');
      showToast('Remarks are mandatory when approving.', 'error', 'Validation Error');
      return;
    }

    // Close the remarks modal immediately so we can show the loading modal
    setApprovalModal({ ...approvalModal, isOpen: false });

    // Start Loading
    setActionModal({ isOpen: true, status: 'loading', message: `${action === 'APPROVED' ? 'Approving' : 'Rejecting'} BOQ...` });

    try {
      const token = localStorage.getItem('authToken');
      const endpoint = action === 'APPROVED' 
        ? `${API_URL}/survey-boq/approve` 
        : `${API_URL}/survey-boq/reject`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ surveyBOQID, remarks })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Action failed');
      }
      
      fetchSurveyBOQs();
      // Show Success
      setActionModal({ isOpen: true, status: 'success', message: `Survey BOQ ${action === 'APPROVED' ? 'Approved' : 'Rejected'} successfully` });
      setTimeout(() => setActionModal({ isOpen: false }), 2000);
    } catch (error) {
      // console.error(`Error:`, error);
      // Show Error
      setActionModal({ isOpen: true, status: 'error', message: `Error: ${error.message}` });
    }
  };

  // Ensure handleSendToBc exists (it does in your provided code, just keep it)


   const handleView = async (boq) => {
    try {
      // 1. Fetch the full details (including Lines) for this specific ID
      const token = localStorage.getItem('authToken'); // or 'authToken' depending on your setup
      const response = await fetch(`${API_URL}/survey-boq/${boq.SurveyBOQID}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch details');
      
      const result = await response.json(); 
      // --- FIX: Flatten the data structure ---
      const normalizedData = {
        ...result.header,
        Lines: result.lines
      };

      // 2. Set this full data to the state
      setSelectedBOQ(normalizedData); 
      setShowViewModal(true);
      
    } catch (error) {
      // console.error('Error fetching details for View:', error);
      // alert('Could not load record details');
      showToast('Could not load record details', 'error', 'Error');
    }
  };

    const handleDelete = async (id) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Survey BOQ',
      message: 'Are you sure you want to delete this Survey BOQ?',
      isDanger: true,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('authToken');
          const response = await fetch(`${API_URL}/survey-boq/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
           const result = await response.json();

        if (!response.ok) {
          showToast(
            result.message || 'Delete not allowed',
            'error',
            'Action Not Allowed'
          );
          return;
        }
          fetchSurveyBOQs();
          showToast('Deleted successfully', 'success', 'Done');
        } catch (error) {
          // console.error('Error deleting:', error);
          showToast('Error deleting record', 'error', 'Error');
        }
      }
    });
  };

    const showToast = (message, type = 'success', title = 'Done', duration = 4000) => {
    setToast({ open: true, message, type, title });
    setTimeout(() => {
      setToast(prev => ({ ...prev, open: false }));
    }, duration);
  };

  const handleLogout = () => { navigate('/login'); };

  // --- ADDED: Handler for BC Status Filter ---
  const handleStatusFilterChange = (filterValue) => {
    setSelectedStatusFilters(prev => 
      prev.includes(filterValue) 
        ? prev.filter(item => item !== filterValue) 
        : [...prev, filterValue]
    );
  };
  // ------------------------------------------

  // Handler to trigger success message
  const handleFormSuccess = (result, actionType) => {
    setTimeout(() => {
      setSuccessModal({
        isOpen: true,
        id: result.SurveyBOQID,
        boqNo: result.SurveyBOQNo,
        action: actionType // 'Created' or 'Updated'
      });
    }, 300); // Small delay to allow form to start closing
  };

  // Handler to close the success message
  const handleCloseSuccess = () => {
    setSuccessModal({ isOpen: false, id: null, boqNo: '', action: '' });
    fetchSurveyBOQs(); // Refresh list
  };

  // Auto-close success modal after 2 seconds
  useEffect(() => {
    if (successModal.isOpen) {
      const timer = setTimeout(() => {
        handleCloseSuccess();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successModal.isOpen]);


 // Filtering Logic (Replace the existing filteredBOQs function)
  const filteredBOQs = surveyBOQs.filter(boq => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = [
      boq.SurveyBOQNo,
      boq.ProjectCode,
      boq.ProjectDescription,
      boq.FeederCode,
      boq.FeederDescription,
      boq.Circle,
      boq.District
    ].some(field => field?.toString().toLowerCase().includes(term));
    
    const matchesStatus = statusFilter === 'All' || boq.Status === statusFilter;
    
    // --- UPDATED: Process Status Filter Logic to match JSON structure ---
    const matchesBcStatus = selectedStatusFilters.length === 0 || selectedStatusFilters.some(filter => {
      
      // 1. Approval Phase (Checking ApprovalStatus string)
      if (filter === 'Pending') return boq.ApprovalStatus === 'PENDING';
      if (filter === 'Approved') return boq.ApprovalStatus === 'APPROVED';
      if (filter === 'Rejected') return boq.ApprovalStatus === 'REJECTED';

      // 2. BC Integration Phase
      // "Sent to BC" covers the final state
      if (filter === 'Send to BC')
        return (
          !boq.SendToBc &&
          (boq.ApprovalStatus === 'APPROVED' || boq.ApprovalStatus == null)
        );

        if (filter === 'Data Successfully Sent') return boq.SendToBc;

      return false;
    });
    // --------------------------------------------------------------

    return matchesSearch && matchesStatus && matchesBcStatus;
  });

  // Sorting Logic
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      let valA = a[sortConfig.key] || '';
      let valB = b[sortConfig.key] || '';
      if (sortConfig.key === 'BOQDate' || sortConfig.key === 'CreatedAt') {
        valA = new Date(valA); valB = new Date(valB);
      } else {
        valA = valA.toString().toLowerCase(); valB = valB.toString().toLowerCase();
      }
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedBOQs = getSortedData(filteredBOQs);
  const totalPages = Math.ceil(sortedBOQs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = sortedBOQs.slice(startIndex, startIndex + itemsPerPage);

  const renderSortArrows = (key) => {
    if (sortConfig.key !== key) return <FaSort className="text-xs ml-1 opacity-50" />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="text-xs ml-1" /> 
      : <FaSortDown className="text-xs ml-1" />;
  };

   const canCreate = hasFullAccess || hasPermission('SURVEY_BOQ', 'CanCreate');
  const canView = hasFullAccess || hasPermission('SURVEY_BOQ', 'CanView');
  const canEdit = hasFullAccess || hasPermission('SURVEY_BOQ', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('SURVEY_BOQ', 'CanDelete');

  // 🔒 SURVEY BOQ LOCK RULES (same as Site Indent & Material Issue)
// const isEditLocked = (boq) =>
//   boq.CurrentLevel >= 2 ||
//   (boq.CurrentLevel === 1 &&
//     ['APPROVED', 'REJECTED'].includes(boq.ApprovalStatus)) ||
//   boq.SendToBc === true;

// const isDeleteLocked = (boq) => {
//   // 1. If Sent to BC, always Lock
//   if (boq.SendToBc === 1 || boq.SendToBc === true) return true;

//   // // 2. If Rejected, Lock (Cannot delete)
//   // if (boq.ApprovalStatus === 'REJECTED') return true;

//   // 3. If Approved, Lock (Cannot delete)
//   if (boq.ApprovalStatus === 'APPROVED') return true;

//   // 4. If Pending, check the Level
//   if (boq.ApprovalStatus === 'PENDING') {
//     // If exactly Level 1, Allow Delete (Unlocked)
//     if (boq.CurrentLevel === 1) return false;
    
//     // If Level > 1 (e.g. Level 2 pending), Lock
//     return true;
//   }

//   // 5. Default (Draft/No Flow), Allow Delete
//   return false;
// };


 const isEditLocked = (boq) => {
    // 1. If Sent to BC, always Locked
    if (boq.SendToBc === true || boq.SendToBc=== 1) return false;

    // 2. If Rejected, always Locked
    if (boq.ApprovalStatus === 'REJECTED') return true;
    
     if (!boq.ApprovalFlowID && !boq.SendToBC) {
      return false;
    }

    // 3. If Fully Approved (All levels done), Locked for EVERYONE
    // This ensures neither entry user nor approvers can edit after final approval.
    // if (boq.ApprovalStatus === 'APPROVED') return true;

    // 4. If Pending Approval
    if (boq.ApprovalStatus === 'PENDING' && boq.CurrentLevel > 1) {
      // 'CanCurrentUserApprove' is calculated in SQL.
      // It is TRUE only if the logged-in user/role matches the CurrentApprover of the active level.
      // It effectively restricts edit rights to ONLY the current level's approver.
      if (boq.CanCurrentUserApprove) {
        return false; // Unlocked for this specific approver
      }
      return true; // Locked for Entry User, Previous Approvers, and everyone else
    }

    // 5. Default (Draft/No Flow) - Unlocked
    return false;
  };


  const isDeleteLocked = (boq) => {
    // "Same case with delete"
    
    // 1. If Rejected, always locked
    // if (item.ApprovalStatus === 'REJECTED') return true;

    // 2. If Current Level > 1, locked
    if (boq.CurrentLevel > 1) return true;

    if (boq.ApprovalStatus === 'APPROVED' && boq.DocumentApprovalID) {
      return true;
    }

    // 3. If Level 1 is Approved, locked
    if (boq.CurrentLevel === 1 && boq.ApprovalStatus === 'APPROVED') return true;

    // 4. Specific Logic: "Approved but no DocumentApprovalID"
    if (boq.ApprovalStatus === 'APPROVED' && !boq.DocumentApprovalID) {
      return boq.SendToBc === true ;
    }

    // 5. Standard Lock: If Sent to BC or Validated
    if (boq.SendToBc === true ) return true;

    return false;
  };



  return (
    <div style={{ minHeight: '100vh', background: COLORS.CLOUD }} className="flex surveyboq-root">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={setSidebarCollapsed}
        theme={{ sidebarBg: '#ffffff', activeBg: COLORS.BABY1, activeText: '#ffffff', profileBg: COLORS.BABY1 }}
        onLogout={handleLogout}
      />
      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}`}>
        
        {/* Header */}
        <div className="mb-8 border-b" style={{ borderColor: COLORS.BORDER, paddingBottom: 12 }}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-semibold" style={{ color: COLORS.TEXT }}>Survey BOQ Management</h1>
              <p className="mt-1" style={{ color: COLORS.MUTED }}>Create and manage Survey Bills of Quantities.</p>
            </div>
             {canCreate && (
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 font-medium transition-all duration-200 shadow-sm"
              style={{ backgroundColor: COLORS.BABY1, color: '#fff' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = COLORS.PRIMARY_HOVER}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = COLORS.BABY1}
            >
              <FaPlus className="w-4 h-4" /> Create New BOQ
            </button> )}
          </div>
        </div>

                {/* Summary Cards - Add this block above the Filters section */}
        {/* Summary Cards */}
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
  {[
    { label: 'Project Qty', value: summaryData.projectQty, icon: 'box', color: 'blue' },
    { label: 'Survey Qty', value: summaryData.surveyQty, icon: 'check', color: 'green' },
    { label: 'Indent Qty', value: summaryData.indentQty, icon: 'file', color: 'purple' },
    { label: 'Pending Approval', value: summaryData.pendingApproval, icon: 'clock', color: 'amber' },
    { label: 'Pending Send to BC', value: summaryData.pendingSendToBC, icon: 'upload', color: 'teal' },
  ].map((stat) => (
    <div
      key={stat.label}
      className="p-4 bg-white border shadow-sm hover:shadow-md transition-shadow duration-200 min-h-[100px] flex flex-col"
      style={{ borderColor: COLORS.BORDER }}
    >
      <div className="flex items-start justify-between flex-1">
        <div className="flex-1 min-w-0 pr-3">
          {/* Label - BIGGER */}
          <p className="text-md font-bold mb-2" style={{ color: COLORS.TEXT }}>
            {stat.label}
          </p>
          {/* Value - SMALLER with WRAP */}
          <p 
            className="text-sm font-semibold leading-snug break-all" 
            style={{ 
              color: COLORS.MUTED,
              wordBreak: 'break-word',
              overflowWrap: 'break-word'
            }}
          >
            {typeof stat.value === 'number' 
              ? stat.value.toLocaleString('en-US', { maximumFractionDigits: 2 })
              : stat.value
            }
          </p>
        </div>
        <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-slate-100 text-slate-600">
          {stat.icon === 'box' && (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          )}
          {stat.icon === 'check' && (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          {stat.icon === 'file' && (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 4H7a2 2 0 01-2-2V5a2 2 0 012-2h10a2 2 0 012 2v14a2 2 0 01-2 2z" />
            </svg>
          )}
          {stat.icon === 'clock' && (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          {stat.icon === 'upload' && (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
          )}
        </div>
      </div>
    </div>
  ))}
</div>

        {/* Filters */}
        <div className="p-5 bg-white mb-6 border" style={{ borderColor: COLORS.BORDER }}>
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold mb-1" style={{ color: COLORS.MUTED }}>Search</label>
              <input
                type="text"
                placeholder="BOQ No, Project, Feeder..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 bg-white border focus:outline-none"
                style={{ borderColor: COLORS.BORDER }}
              />
            </div>
            {/* <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: COLORS.MUTED }}>Status</label>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="px-4 py-2 bg-white border focus:outline-none w-full md:w-auto"
                style={{ borderColor: COLORS.BORDER }}
              >
                <option value="Open">Open</option>
                <option value="Released">Released</option>
                <option value="Canceled">Canceled</option>
                <option value="Rejected">Rejected</option>
                <option value="Approved">Approved</option>
              </select>
            </div> */}
            <div className="">
            <label className="block text-sm font-semibold mb-2" style={{ color: COLORS.MUTED }}>Process Status</label>
            <div className="flex flex-wrap gap-4 text-sm px-4 py-2 bg-white border focus:outline-none w-full md:w-auto" style={{ borderColor: COLORS.BORDER }}>
              {['Pending', 'Approved', 'Rejected', 'Send to BC', 'Data Successfully Sent'].map((label) => (
                <label key={label} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    checked={selectedStatusFilters.includes(label)}
                    onChange={() => handleStatusFilterChange(label)}
                  />
                  <span style={{ color: COLORS.TEXT }}>{label}</span>
                </label>
              ))}
            </div>
          </div>
          </div>

          {/* --- ADDED: BC Status Filter UI --- */}
          
          {/* -------------------------------- */}
        </div>

        {/* Table */}
        <section className="bg-white border" style={{ borderColor: COLORS.BORDER }}>
          <div className="flex justify-between items-center p-4 border-b" style={{ borderColor: COLORS.BORDER }}>
            <h2 className="text-xl font-semibold" style={{ color: COLORS.TEXT }}>Survey BOQ List</h2>
            <div className="flex items-center space-x-2">
               <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2" style={{color: currentPage === 1 ? '#ccc' : COLORS.TEXT}}><FaChevronLeft /></button>
               <span style={{color: COLORS.TEXT}}>{currentPage} / {totalPages || 1}</span>
               <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} className="p-2" style={{color: currentPage === totalPages || totalPages === 0 ? '#ccc' : COLORS.TEXT}}><FaChevronRight /></button>
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : currentItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No records found</div>
            ) : (
              <table className="w-full">
                <thead className="text-xs font-semibold text-left uppercase" style={{ backgroundColor: COLORS.BABY, color: COLORS.TEXT, borderBottom: `2px solid ${COLORS.DIVIDER}` }}>
                  <tr>
                    <th className="px-4 py-3 cursor-pointer" onClick={() => setSortConfig({ key: 'SurveyBOQNo', direction: sortConfig.key === 'SurveyBOQNo' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })}>
                      BOQ No {renderSortArrows('SurveyBOQNo')}
                    </th>
                    <th className="px-4 py-3 cursor-pointer" onClick={() => setSortConfig({ key: 'BOQDate', direction: sortConfig.key === 'BOQDate' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })}>
                      Date {renderSortArrows('BOQDate')}
                    </th>
                    <th className="px-4 py-3">Project</th>
                    <th className="px-4 py-3">Feeder</th>
                    <th className="px-4 py-3">Circle / District</th>
                    {/* <th className="px-4 py-3">Status</th> */}
                    <th className="px-4 py-3">Approval/BC Status</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((boq, idx) => (
                    <tr key={boq.SurveyBOQID || idx} className="hover:bg-[#faf9f8] transition-colors">
                      <td className="px-4 py-3 font-medium" style={{ color: COLORS.TEXT }}>{boq.SurveyBOQNo}</td>
                      <td className="px-4 py-3" style={{ color: COLORS.MUTED }}>{formatDate(boq.BOQDate)}</td>
                      <td className="px-4 py-3" style={{ color: COLORS.MUTED }}>
                        <div className="font-medium text-black">{boq.ProjectCode}</div>
                         <div className="text-xs text-gray-500 truncate max-w-[200px]">{boq.ProjectDescription || '-'}</div>
                        {/* <div className="text-xs">ID: {boq.ProjectID}</div> */}
                      </td>
                      <td className="px-4 py-3" style={{ color: COLORS.MUTED }}>
                          <div className="font-medium text-black">{boq.FeederCode || '-'}</div>
                           <div className="text-xs text-gray-500 truncate max-w-[200px]">{boq.FeederDescription || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: COLORS.MUTED }}>
                        <div>{boq.Circle}</div>
                        <div className="text-xs">{boq.District}</div>
                      </td>
                      {/* <td className="px-4 py-3">
                        <span className={`status-badge ${
                          boq.Status === 'Open' ? 'bg-green-100 text-green-800' : 
                          boq.Status === 'Approved' ? 'bg-grey-100 text-grey-800' :
                          boq.Status === 'Released' ? 'bg-blue-100 text-blue-800' : 
                          boq.Status === 'Canceled' ? 'bg-red-100 text-red-800' : 
                          boq.Status === 'Rejected' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-gray-100 text-gray-800' 
                        }`}>
                          {boq.Status || 'Draft'}
                        </span>
                      </td> */}
                      <td className='px-4 py-3'>
  {/*
    LOGIC MIRRORING MRN:
    1. PENDING    -> Approval flow (Check CanCurrentUserApprove)
    2. REJECTED   -> Show Rejected only
    3. APPROVED / NULL -> Send to BC flow
  */}

  {boq.ApprovalStatus === 'PENDING' ? (
    // --- APPROVAL PHASE ---
    boq.CanCurrentUserApprove ? (
      <button 
        onClick={() => openApprovalModal(boq.SurveyBOQID, boq.CurrentLevel)}
        className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded text-white transition-colors"
        style={{ backgroundColor: '#16A34A' }} // Green
      >
        Approve (Lvl {boq.CurrentLevel})
      </button>
    ) : (
      <span className="text-xs text-gray-500 italic flex items-center gap-1">
        Approval is going on (Lvl {boq.CurrentLevel})
      </span>
    )

  ) : boq.ApprovalStatus === 'REJECTED' ? (
    // --- REJECTED STATE ---
    <span className="text-xs font-semibold text-red-600 flex items-center gap-1 bg-red-50 px-2 py-1 rounded border border-red-200">
      Rejected
    </span>

  ) : (
    // --- POST-APPROVAL PHASE ---
    // Note: MRN had 'Validate Data' step, Survey seems to go straight to 'Send To BC'
    // based on your existing code. If you want Validate, add it here like MRN.
    <>
      {/* If the Approval workflow is done (or doesn't exist) but not yet sent to BC */}
      {(!boq.SendToBc || boq.SendToBc === 0) && (
        <button 
          onClick={() => handleSendToBc(boq)}
          className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded text-white transition-colors"
          style={{ backgroundColor: '#2563EB' }} // Blue
          title="Send to BC"
        >
          Send to BC
        </button>
      )}

      {/* If Sent to BC */}
      {(boq.SendToBc === 1 || boq.SendToBc === true) && (
        <span className="text-xs font-semibold text-green-600 flex items-center gap-1 bg-green-50 px-2 py-1 rounded border border-green-200">
          Sent to BC successfully.
        </span>
      )}
    </>
  )}
</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center items-center space-x-3" style={{ color: COLORS.MUTED }}>
                          
                          

                         {canView && (<button onClick={() => handleView(boq)} title="View" className="hover:text-black"><FaEye /></button>)} 
                         {/* {canEdit && (<button onClick={() => handleEdit(boq)} title="Edit" className="hover:text-blue-600"><FaEdit /></button>)} */}

                                                {canEdit && (
                           <button
                             onClick={() => !isEditLocked(boq) && handleEdit(boq)}
                             disabled={isEditLocked(boq)}
                             title={
                               isEditLocked(boq)
                                 ? 'Edit disabled after approval or rejection'
                                 : 'Edit'
                             }
                             className={`${
                               isEditLocked(boq)
                                 ? 'cursor-not-allowed opacity-40'
                                 : 'hover:text-blue-600'
                             }`}
                           >
                             <FaEdit />
                           </button>
                         )}
    

                          {canDelete && (
  <button
    onClick={() =>
      !isDeleteLocked(boq) &&
      handleDelete(boq.SurveyBOQID)
    }
    disabled={isDeleteLocked(boq)}
    title={
      isDeleteLocked(boq)
        ? 'Delete disabled after approval / rejection / BC'
        : 'Delete'
    }
    className={`${
      isDeleteLocked(boq)
        ? 'cursor-not-allowed opacity-40'
        : 'hover:text-red-600'
    }`}
  >
    <FaTrash />
  </button>
)}

                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </main>

      {/* FORM MODAL */}
      {showFormModal && (
        <SurveyBOQFormModal
          open={showFormModal}
          onClose={() => setShowFormModal(false)}
          initialData={selectedBOQ}
          onSubmit={fetchSurveyBOQs}
          onFormSuccess={handleFormSuccess}
        />
      )}

      {/* VIEW MODAL */}
      {showViewModal && selectedBOQ && (
        <SurveyBOQViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          data={selectedBOQ}
        />
      )}

      
 

        {/* Approval/Reject Modal */}
      {approvalModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-50">
          <div className="relative bg-white shadow-xl max-w-md w-full mx-4 p-6 rounded-lg animate-in fade-in zoom-in duration-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Review BOQ: {approvalModal.surveyBOQID} (Level {approvalModal.currentLevel})
            </h3>
            
            {/* Remarks Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remarks {<span className="text-red-500">*</span>}
              </label>
              <textarea
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Enter remarks..."
                value={approvalModal.remarks}
                onChange={(e) => setApprovalModal({ ...approvalModal, remarks: e.target.value })}
              ></textarea>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between gap-3">
              <button
                onClick={() => setApprovalModal({ ...approvalModal, isOpen: false })}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                Cancel
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => handleApprovalAction('REJECTED')}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Reject
                </button>
                <button
                  onClick={() => handleApprovalAction('APPROVED')}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                >
                  Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

            {/* --- SUCCESS MODAL --- */}
      {successModal.isOpen && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] animate-slideDown">
          <div className="flex items-center gap-4 bg-white border border-green-200 shadow-xl rounded-xl px-6 py-4 min-w-[420px]">
            {/* Icon */}
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <SuccessIcon size={22} />
            </div>

            {/* Text */}
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-800">
                Survey BOQ {successModal.action} Successfully
              </h4>
              <p className="text-xs text-gray-500">
                BOQ No: <span className="font-medium">{successModal.boqNo}</span>
              </p>
            </div>

            {/* Loader bar */}
            <div className="absolute bottom-0 left-0 h-[3px] bg-green-500 rounded-b-xl animate-progress" />
          </div>

          <style>
            {`
              @keyframes slideDown {
                from { opacity: 0; transform: translate(-50%, -10px); }
                to { opacity: 1; transform: translate(-50%, 0); }
              }
              @keyframes progress {
                from { width: 100%; }
                to { width: 0%; }
              }
              .animate-slideDown { animation: slideDown 0.25s ease-out; }
              .animate-progress { animation: progress 2s linear; }

            `}
          </style>
        </div>
      )}

      {toast.open && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[99999] animate-toastIn">
          <div className={`
            relative w-[420px] rounded-2xl shadow-2xl border backdrop-blur-md overflow-hidden
            ${toast.type === 'error' && 'bg-red-50/90 border-red-200'}
            ${toast.type === 'warning' && 'bg-amber-50/90 border-amber-200'}
            ${toast.type === 'success' && 'bg-green-50/90 border-green-200'}
          `}>
            <div className="flex gap-4 px-5 py-4">
              {/* Icon */}
              <div className="pt-1">
                {toast.type === 'error' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-100">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </div>
                )}
                {toast.type === 'warning' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </div>
                )}
                {toast.type === 'success' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-green-100">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                )}
              </div>
              {/* Text */}
              <div className="flex-1">
                <div className="font-semibold text-sm text-gray-800 mb-1">{toast.title}</div>
                <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{toast.message}</div>
              </div>
            </div>
            {/* Progress bar */}
            <div className={`
                absolute bottom-0 left-0 h-[3px] animate-toastProgress
                ${toast.type === 'error' && 'bg-red-500'}
                ${toast.type === 'warning' && 'bg-amber-500'}
                ${toast.type === 'success' && 'bg-green-500'}
              `}
            />
          </div>
          <style>
            {`
             
                /* Add this to the end of your existing style.innerHTML string */
                @keyframes toastIn {
                  from { opacity: 0; transform: translate(-50%, -16px) scale(0.98); }
                  to { opacity: 1; transform: translate(-50%, 0) scale(1); }
                }
                @keyframes toastProgress {
                  from { width: 100%; }
                  to { width: 0%; }
                }
                .animate-toastIn { animation: toastIn 0.25s ease-out; }
                .animate-toastProgress { animation: toastProgress 4s linear forwards; }

            `}
          </style>
        </div>
      )}

  <ActionModal 
        isOpen={actionModal.isOpen}
        status={actionModal.status}
        message={actionModal.message}
        onClose={() => setActionModal({ ...actionModal, isOpen: false })}
      />

      <ConfirmationDialog 
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={() => {
          if (confirmModal.onConfirm) confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        isDanger={confirmModal.isDanger}
      />
    
    </div>
  );
};

export default SurveyBOQItems;